<template>
    <article class="patient-review">
        <header>
            <svg class="svg-logo patient-review__logo">
                <use xlink:href="#picture-logo" />
            </svg>
        </header>
        <main v-if="isUrlValid">
            <div v-if="!areRatingsEntered && showRatingInstruction" class="rating-description">
                <h4 v-if="practiceType === PracticeTypeEnum.PHYSICIAN">
                    {{ $t('pages.referrals.patient_review.please_rate_physician', { referredToName: referredToName }) }}
                </h4>
                <h4 v-else>
                    {{ $t('pages.referrals.patient_review.please_rate_ancillary', { referredToName: referredToName }) }}
                </h4>
                <p>{{ $t('pages.referrals.patient_review.rate_on_scale_1') }}</p>
                <p>{{ $t('pages.referrals.patient_review.rate_on_scale_5') }}</p>
            </div>

            <ratings-enter
                v-show="!areRatingsEntered"
                :ratings="ratings"
                @ratings-entered="ratingsEntered"
                @first-rating-entered="firstRatingEntered"
            ></ratings-enter>

            <ratings-summary
                v-if="areRatingsEntered"
                :ratingsWithGrades="ratings"
                :summaryText="summaryText"
                :maxGrade="5"
                :numberedList="true"
            ></ratings-summary>
        </main>
        <main v-else>
            <div class="patient-review--invalid">
                <common-page-header
                    :heading="'Token has expired!'"
                    size="large"
                    contentSpacing="small"
                    :isCenter="true"
                ></common-page-header>
                <common-icon name="error" size="ultra-large"></common-icon>
            </div>
        </main>
    </article>
</template>

<script>
    import RatingsEnter from 'components/page-elements/ratings/RatingsEnter'
    import RatingsSummary from 'components/page-elements/ratings/RatingsSummary'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

    export default {
        components: {
            RatingsEnter,
            RatingsSummary,
        },

        data() {
            return {
                isUrlValid: true,
                maxGrade: 5,
                referredToName: this.$route.query.f || '',
                physician: {
                    firstName: this.$route.query.fn || '',
                    lastName: this.$route.query.ln || '',
                    speciality: this.$route.query.sp || '',
                },
                summaryText: '',
                ratings: [],
                areRatingsEntered: false,
                areRatingsSaved: false,
                grades: null,
                PracticeTypeEnum: PracticeTypeEnum,
                practiceType: null,
                ancillary: {
                    name: '',
                },
                showRatingInstruction: true,
            }
        },
        methods: {
            ratingsEntered(grades) {
                this.areRatingsEntered = true
                this.grades = grades

                this.ratings.forEach((rating) => {
                    rating.grade = this.grades[rating.key]
                })

                ReferralsAPI.updatePatientReview(
                    this.$route.query.rId,
                    this.practiceType,
                    this.grades,
                    this.$route.query.s,
                    Number(this.$route.query.ts),
                )
                    .then(() => {
                        this.areRatingsSaved = true
                    })
                    .catch((err) => {
                        let dataMsg = err.response && err.response.data && err.response.data.message
                        let responseMsg = err.response && err.response.message
                        let errMsg = err.message

                        let msg = dataMsg || responseMsg || errMsg
                        if (msg && typeof msg === 'string') alert('Rating upload failed: ' + msg)
                        else alert('Rating upload failed!')
                    })
            },

            checkSignature(rId, s, ts) {
                ReferralsAPI.checkSignatureValidity(rId, s, ts)
                    .then((response) => {
                        this.practiceType = response.practiceType
                        this.setRatingQuestions()
                        if (this.practiceType === PracticeTypeEnum.PHYSICIAN) {
                            this.referredToName = `${response.firstName} ${response.lastName}, ${response.designation}`
                        } else {
                            this.referredToName = response.name
                        }
                        this.summaryText = `${this.$t('pages.referrals.patient_review.thank_you_for_rating', {
                            referredToName: this.referredToName || '',
                        })}!`
                    })
                    .catch(() => {
                        this.isUrlValid = false
                    })
            },

            setRatingQuestions() {
                if (this.practiceType === PracticeTypeEnum.PHYSICIAN) {
                    this.ratings = [
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.office.text'),
                            question: this.$t('pages.referrals.patient_review.ratings.physician.office.question'),
                            key: 'officeEnvironment',
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.staff.text'),
                            question: this.$t('pages.referrals.patient_review.ratings.physician.staff.question'),
                            key: 'staffFriendliness',
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.wait_time.text'),
                            question: this.$t('pages.referrals.patient_review.ratings.physician.wait_time.question'),
                            key: 'waitTime',
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.trust.text'),
                            question: this.$t('pages.referrals.patient_review.ratings.physician.trust.question'),
                            key: 'levelOfTrust',
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.explanation.text'),
                            question: this.$t('pages.referrals.patient_review.ratings.physician.explanation.question'),
                            key: 'providerExplanation',
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.listening.text'),
                            question: this.$t('pages.referrals.patient_review.ratings.physician.listening.question'),
                            key: 'providerListening',
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.time_spent.text'),
                            question: this.$t('pages.referrals.patient_review.ratings.physician.time_spent.question'),
                            key: 'timeSpent',
                        },
                    ]
                } else {
                    this.ratings = [
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.ancillary.facility.text'),
                            question: '',
                            key: 'facilityEnv',
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.ancillary.staff.text'),
                            question: '',
                            key: 'staffFriendliness',
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.ancillary.wait_time.text'),
                            question: '',
                            key: 'totalWaitTime',
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.ancillary.refer.text'),
                            question: '',
                            key: 'likelyRefer',
                        },
                    ]
                }
            },

            firstRatingEntered() {
                this.showRatingInstruction = false
            },
        },

        mounted() {
            if (this.$route.query.rId && this.$route.query.s && this.$route.query.ts) {
                this.checkSignature(this.$route.query.rId, this.$route.query.s, this.$route.query.ts)
            }
        },
    }
</script>

<style scoped>
    .svg-logo {
        width: 133px;
        height: 70px;
        margin: 10px;
    }
    .refferedToDoctorDetails {
        padding-bottom: 40px;
    }
    .rating-description {
        padding: 0 10px;
        padding-bottom: 40px;
    }
    main {
        text-align: center;
    }
    h4 {
        font-family: Poppins;
        font-size: 22px;
        text-align: center;
        margin-bottom: 1rem;
        color: #333333;
        padding-top: 30px;
    }
    .physician-center {
        text-align: center;
        padding-bottom: 40px;
    }
    .physician-inner {
        display: inline-block;
        text-align: left;
    }

    .patient-review--invalid {
        margin-top: 50px;
        font-weight: bold;
    }
</style>
